/**
 * Plan Restrictions Test Page
 *
 * A comprehensive showcase of all plan restriction components and examples.
 * Use this page with window.devTools.setPlanOverride() to test different plan levels.
 *
 * Usage:
 * 1. Navigate to /dev/plan-restrictions-test
 * 2. Open browser console
 * 3. Try: window.devTools.setPlanOverride('FREE')
 * 4. Reload and see how components change
 */

import { createFileRoute } from '@tanstack/react-router';
import { useAllFeatureFlags } from '@/hooks/useFeatureFlag';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  WorkspaceLimitExample
} from '@/components/plan-restrictions/examples/WorkspaceLimitExample';
import {
  ExportButtonExample,
  CommentsButtonExample,
  BranchingButtonExample,
  ShareLinksButtonExample,
} from '@/components/plan-restrictions/examples/PlanLockedButtonExamples';
import {
  SettingsChecksExample,
  SettingsAuthExample,
  SettingsBrandingExample,
  SettingsReviewExample,
  VideoEditorExample,
} from '@/components/plan-restrictions/examples/FeatureListItemExamples';
import {
  MAULimitCardExample,
  MAULimitCardUnavailableExample,
  MAULimitCardUnlimitedExample,
  InlineFeatureLockCardExample,
  InlineFeatureLockBannerExample,
  InlineFeatureLockInlineExample,
  InlineFeatureLockWithImageExample,
} from '@/components/plan-restrictions/examples/AdvancedComponentExamples';
import {
  FeatureGateBasicExample,
  FeatureGateWithFallbackExample,
  FeatureGateWithUpgradeExample,
  MultiFeatureGateAllExample,
  MultiFeatureGateAnyExample,
  PlanGateBasicExample,
  PlanGateWithUpgradeExample,
} from '@/components/plan-restrictions/examples/FeatureGateExamples';
import { Copy, RefreshCw } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/(protected)/dev/plan-restrictions-test')({
  component: PlanRestrictionsTestPage,
});

function PlanRestrictionsTestPage() {
  const { plan, loading } = useAllFeatureFlags();
  const [copied, setCopied] = useState<string | null>(null);

  const copyCommand = (command: string) => {
    navigator.clipboard.writeText(command);
    setCopied(command);
    setTimeout(() => setCopied(null), 2000);
  };

  const planCommands = [
    { plan: 'FREE', command: 'window.devTools.setPlanOverride("FREE")' },
    { plan: 'STARTER', command: 'window.devTools.setPlanOverride("STARTER")' },
    { plan: 'TEAM', command: 'window.devTools.setPlanOverride("TEAM")' },
    { plan: 'PRO', command: 'window.devTools.setPlanOverride("PRO")' },
    { plan: 'ENTERPRISE', command: 'window.devTools.setPlanOverride("ENTERPRISE")' },
  ];

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="text-muted-foreground">Loading feature flags...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-6xl space-y-8 p-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold">Plan Restrictions Test Page</h1>
        <p className="text-muted-foreground">
          Test all plan restriction components at different subscription tiers using the dev tools.
        </p>
      </div>

      {/* Component Usage Guide */}
      <Card className="border-primary/50">
        <CardHeader>
          <CardTitle>Component Usage Guide</CardTitle>
          <CardDescription>
            Quick reference for all plan restriction components
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Hide/Show Sections */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">🎭 Hide/Show Entire Sections</h3>
            <div className="bg-muted rounded-md p-3 space-y-2">
              <div className="text-xs space-y-1">
                <div className="font-mono text-xs">
                  <span className="text-blue-600">&lt;FeatureGate</span>{' '}
                  <span className="text-purple-600">flag=</span>
                  <span className="text-green-600">"export"</span>
                  <span className="text-blue-600">&gt;</span>
                </div>
                <div className="pl-4 font-mono text-xs text-muted-foreground">
                  &lt;ExportDashboard /&gt;
                </div>
                <div className="font-mono text-xs">
                  <span className="text-blue-600">&lt;/FeatureGate&gt;</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                ✅ Use when: You want to completely hide a section based on feature flag
              </p>
            </div>
            <div className="bg-muted rounded-md p-3 space-y-2">
              <div className="text-xs space-y-1">
                <div className="font-mono text-xs">
                  <span className="text-blue-600">&lt;PlanGate</span>{' '}
                  <span className="text-purple-600">requiredPlan=</span>
                  <span className="text-green-600">"TEAM"</span>
                  <span className="text-blue-600">&gt;</span>
                </div>
                <div className="pl-4 font-mono text-xs text-muted-foreground">
                  &lt;TeamFeatures /&gt;
                </div>
                <div className="font-mono text-xs">
                  <span className="text-blue-600">&lt;/PlanGate&gt;</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                ✅ Use when: You want to hide a section based on plan level
              </p>
            </div>
          </div>

          {/* Buttons with Locks */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">🔒 Buttons with Lock Icons</h3>
            <div className="bg-muted rounded-md p-3 space-y-2">
              <div className="text-xs space-y-1">
                <div className="font-mono text-xs">
                  <span className="text-blue-600">&lt;PlanLockedButton</span>
                </div>
                <div className="pl-4 font-mono text-xs">
                  <span className="text-purple-600">featureFlag=</span>
                  <span className="text-green-600">"export"</span>
                </div>
                <div className="pl-4 font-mono text-xs">
                  <span className="text-purple-600">featureName=</span>
                  <span className="text-green-600">"Export Documentation"</span>
                </div>
                <div className="pl-4 font-mono text-xs">
                  <span className="text-purple-600">onClick=</span>
                  <span className="text-orange-600">{'{handleExport}'}</span>
                </div>
                <div className="font-mono text-xs">
                  <span className="text-blue-600">&gt;</span>
                  <span className="text-muted-foreground">Export</span>
                  <span className="text-blue-600">&lt;/PlanLockedButton&gt;</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                ✅ Use when: Button action requires specific feature/plan
              </p>
            </div>
          </div>

          {/* Settings Toggles */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">⚙️ Settings Page Toggles</h3>
            <div className="bg-muted rounded-md p-3 space-y-2">
              <div className="text-xs space-y-1">
                <div className="font-mono text-xs">
                  <span className="text-blue-600">&lt;FeatureListItem</span>
                </div>
                <div className="pl-4 font-mono text-xs">
                  <span className="text-purple-600">title=</span>
                  <span className="text-green-600">"Broken Link Checker"</span>
                </div>
                <div className="pl-4 font-mono text-xs">
                  <span className="text-purple-600">featureFlag=</span>
                  <span className="text-green-600">"checks"</span>
                </div>
                <div className="pl-4 font-mono text-xs">
                  <span className="text-purple-600">enabled=</span>
                  <span className="text-orange-600">{'{enabled}'}</span>
                </div>
                <div className="pl-4 font-mono text-xs">
                  <span className="text-purple-600">onToggle=</span>
                  <span className="text-orange-600">{'{setEnabled}'}</span>
                </div>
                <div className="font-mono text-xs">
                  <span className="text-blue-600">/&gt;</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                ✅ Use when: Settings page feature that can be toggled on/off
              </p>
            </div>
          </div>

          {/* Limit Indicators */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">📊 Usage Limits & Progress</h3>
            <div className="bg-muted rounded-md p-3 space-y-2">
              <div className="text-xs space-y-1">
                <div className="font-mono text-xs">
                  <span className="text-blue-600">&lt;LimitProgressCard</span>
                </div>
                <div className="pl-4 font-mono text-xs">
                  <span className="text-purple-600">limitType=</span>
                  <span className="text-green-600">"topics"</span>
                </div>
                <div className="pl-4 font-mono text-xs">
                  <span className="text-purple-600">current=</span>
                  <span className="text-orange-600">{'{15}'}</span>
                </div>
                <div className="pl-4 font-mono text-xs">
                  <span className="text-purple-600">max=</span>
                  <span className="text-orange-600">{'{50}'}</span>
                </div>
                <div className="pl-4 font-mono text-xs">
                  <span className="text-purple-600">planName=</span>
                  <span className="text-green-600">"STARTER"</span>
                </div>
                <div className="font-mono text-xs">
                  <span className="text-blue-600">/&gt;</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                ✅ Use when: Showing workspace/topic limits in sidebar
              </p>
            </div>
            <div className="bg-muted rounded-md p-3 space-y-2">
              <div className="text-xs space-y-1">
                <div className="font-mono text-xs">
                  <span className="text-blue-600">&lt;MAULimitCard</span>
                </div>
                <div className="pl-4 font-mono text-xs">
                  <span className="text-purple-600">currentMAU=</span>
                  <span className="text-orange-600">{'{350}'}</span>
                </div>
                <div className="pl-4 font-mono text-xs">
                  <span className="text-purple-600">includedMAU=</span>
                  <span className="text-orange-600">{'{500}'}</span>
                </div>
                <div className="pl-4 font-mono text-xs">
                  <span className="text-purple-600">plan=</span>
                  <span className="text-green-600">"PRO"</span>
                </div>
                <div className="font-mono text-xs">
                  <span className="text-blue-600">/&gt;</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                ✅ Use when: Showing Monthly Active Users limit
              </p>
            </div>
          </div>

          {/* Full Feature Replacement */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">🚫 Replace Locked Features</h3>
            <div className="bg-muted rounded-md p-3 space-y-2">
              <div className="text-xs space-y-1">
                <div className="font-mono text-xs">
                  <span className="text-blue-600">&lt;InlineFeatureLock</span>
                </div>
                <div className="pl-4 font-mono text-xs">
                  <span className="text-purple-600">featureName=</span>
                  <span className="text-green-600">"Image Editor"</span>
                </div>
                <div className="pl-4 font-mono text-xs">
                  <span className="text-purple-600">requiredPlan=</span>
                  <span className="text-green-600">"TEAM"</span>
                </div>
                <div className="pl-4 font-mono text-xs">
                  <span className="text-purple-600">variant=</span>
                  <span className="text-green-600">"card"</span>
                </div>
                <div className="font-mono text-xs">
                  <span className="text-blue-600">/&gt;</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                ✅ Use when: Replacing entire feature UI with upgrade prompt
              </p>
            </div>
          </div>

          {/* Quick Decision Tree */}
          <div className="space-y-2 border-t pt-4">
            <h3 className="text-sm font-semibold">🤔 Which Component to Use?</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-start gap-2">
                <span className="font-semibold min-w-24">Hide section:</span>
                <span className="text-muted-foreground">
                  Use <code className="bg-background px-1 rounded">FeatureGate</code> or{' '}
                  <code className="bg-background px-1 rounded">PlanGate</code>
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold min-w-24">Lock button:</span>
                <span className="text-muted-foreground">
                  Use <code className="bg-background px-1 rounded">PlanLockedButton</code>
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold min-w-24">Settings toggle:</span>
                <span className="text-muted-foreground">
                  Use <code className="bg-background px-1 rounded">FeatureListItem</code>
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold min-w-24">Show limits:</span>
                <span className="text-muted-foreground">
                  Use <code className="bg-background px-1 rounded">LimitProgressCard</code> or{' '}
                  <code className="bg-background px-1 rounded">MAULimitCard</code>
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold min-w-24">Replace feature:</span>
                <span className="text-muted-foreground">
                  Use <code className="bg-background px-1 rounded">InlineFeatureLock</code>
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Plan Status */}
      <Card className="border-primary">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Current Plan</span>
            <Badge variant="default" className="text-lg px-3 py-1">
              {plan}
            </Badge>
          </CardTitle>
          <CardDescription>
            Use the commands below to test different plan levels
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="text-sm font-medium">Quick Plan Override:</div>
            <div className="flex flex-wrap gap-2">
              {planCommands.map(({ plan: testPlan, command }) => (
                <Button
                  key={testPlan}
                  variant={plan === testPlan ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    copyCommand(command);
                    // eslint-disable-next-line no-console
                    console.log(
                      `Copied: ${command}\nPaste in console and reload the page`
                    );
                  }}
                >
                  <Copy className="mr-2 h-3 w-3" />
                  {testPlan}
                  {copied === command && ' ✓'}
                </Button>
              ))}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const clearCommand = 'window.devTools.clearPlanOverride()';
                  copyCommand(clearCommand);
                  // eslint-disable-next-line no-console
                  console.log(
                    `Copied: ${clearCommand}\nPaste in console and reload the page`
                  );
                }}
              >
                <RefreshCw className="mr-2 h-3 w-3" />
                Clear Override
                {copied === 'window.devTools.clearPlanOverride()' && ' ✓'}
              </Button>
            </div>
          </div>

          <div className="bg-muted rounded-md p-4 space-y-2">
            <div className="text-sm font-medium">Steps:</div>
            <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
              <li>Click a plan button above to copy the command</li>
              <li>Open browser console (F12)</li>
              <li>Paste and press Enter</li>
              <li>Reload the page (Cmd+R / Ctrl+R)</li>
              <li>See how components change for that plan level</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Phase 2: Core Components - PlanLockedButton */}
      <section className="space-y-4">
        <div>
          <h2 className="text-2xl font-semibold">Phase 2: PlanLockedButton Examples</h2>
          <p className="text-muted-foreground text-sm">
            Buttons that show lock icons and upgrade dialogs when features are restricted
          </p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Action Buttons</CardTitle>
            <CardDescription>
              Test export, comments, branching, and sharing functionality
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <ExportButtonExample />
              <CommentsButtonExample />
              <BranchingButtonExample />
              <ShareLinksButtonExample />
            </div>
          </CardContent>
        </Card>
      </section>

      <Separator />

      {/* Phase 3: Settings Integration - FeatureListItem */}
      <section className="space-y-4">
        <div>
          <h2 className="text-2xl font-semibold">Phase 3: FeatureListItem Examples</h2>
          <p className="text-muted-foreground text-sm">
            Settings page feature toggles with plan badges and upgrade prompts
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Automated Checks</CardTitle>
            </CardHeader>
            <CardContent>
              <SettingsChecksExample />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Authentication</CardTitle>
            </CardHeader>
            <CardContent>
              <SettingsAuthExample />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Branding</CardTitle>
            </CardHeader>
            <CardContent>
              <SettingsBrandingExample />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Review Workflow</CardTitle>
            </CardHeader>
            <CardContent>
              <SettingsReviewExample />
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Kill-Switched Features</CardTitle>
            <CardDescription>
              Features that are temporarily disabled with "Coming Soon" badge
            </CardDescription>
          </CardHeader>
          <CardContent>
            <VideoEditorExample />
          </CardContent>
        </Card>
      </section>

      <Separator />

      {/* Phase 3: Workspace Limits */}
      <section className="space-y-4">
        <div>
          <h2 className="text-2xl font-semibold">Phase 3: LimitProgressCard Examples</h2>
          <p className="text-muted-foreground text-sm">
            Workspace and topic limit indicators for sidebars
          </p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Workspace Sidebar Limits</CardTitle>
            <CardDescription>
              Shows current usage vs. plan limits
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-w-xs">
              <WorkspaceLimitExample />
            </div>
          </CardContent>
        </Card>
      </section>

      <Separator />

      {/* Phase 4: Feature Gates - FeatureGate, PlanGate */}
      <section className="space-y-4">
        <div>
          <h2 className="text-2xl font-semibold">Phase 4: FeatureGate & PlanGate</h2>
          <p className="text-muted-foreground text-sm">
            Hide/show entire sections based on feature flags or plan levels
          </p>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>FeatureGate - Basic</CardTitle>
              <CardDescription>
                Conditionally render sections based on feature flags
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FeatureGateBasicExample />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>FeatureGate - With Fallback</CardTitle>
              <CardDescription>
                Show locked placeholder when feature unavailable
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FeatureGateWithFallbackExample />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>FeatureGate - With Upgrade Prompt</CardTitle>
              <CardDescription>
                Click locked placeholder to see upgrade options
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FeatureGateWithUpgradeExample />
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>MultiFeatureGate - ALL Mode</CardTitle>
                <CardDescription>
                  Requires ALL specified features
                </CardDescription>
              </CardHeader>
              <CardContent>
                <MultiFeatureGateAllExample />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>MultiFeatureGate - ANY Mode</CardTitle>
                <CardDescription>
                  Requires ANY of specified features
                </CardDescription>
              </CardHeader>
              <CardContent>
                <MultiFeatureGateAnyExample />
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>PlanGate - Basic</CardTitle>
              <CardDescription>
                Hide sections based on plan level
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PlanGateBasicExample />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>PlanGate - With Upgrade Prompt</CardTitle>
              <CardDescription>
                Show upgrade dialog for locked plans
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PlanGateWithUpgradeExample />
            </CardContent>
          </Card>
        </div>
      </section>

      <Separator />

      {/* Phase 5: Advanced Components */}
      <section className="space-y-4">
        <div>
          <h2 className="text-2xl font-semibold">Phase 5: Advanced Components</h2>
          <p className="text-muted-foreground text-sm">
            MAU limit cards and inline feature locks
          </p>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>MAU Limit Cards</CardTitle>
              <CardDescription>
                Monthly Active Users tracking and limits
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-sm font-medium mb-2">Normal Usage</h3>
                <MAULimitCardExample />
              </div>
              <div>
                <h3 className="text-sm font-medium mb-2">Tracking Unavailable</h3>
                <MAULimitCardUnavailableExample />
              </div>
              <div>
                <h3 className="text-sm font-medium mb-2">Unlimited Plan</h3>
                <MAULimitCardUnlimitedExample />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>InlineFeatureLock - Card Variant</CardTitle>
              <CardDescription>
                Full feature replacement when locked
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <InlineFeatureLockCardExample />
              <InlineFeatureLockWithImageExample />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>InlineFeatureLock - Banner Variant</CardTitle>
              <CardDescription>
                Compact notification in toolbar/header
              </CardDescription>
            </CardHeader>
            <CardContent>
              <InlineFeatureLockBannerExample />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>InlineFeatureLock - Inline Variant</CardTitle>
              <CardDescription>
                Minimal inline prompt in compact spaces
              </CardDescription>
            </CardHeader>
            <CardContent>
              <InlineFeatureLockInlineExample />
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer with instructions */}
      <Card className="bg-muted">
        <CardHeader>
          <CardTitle>Testing Checklist</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <p className="font-medium">Test each plan level:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>FREE - Most features locked, should show many upgrade prompts</li>
              <li>STARTER - Some features unlocked, moderate restrictions</li>
              <li>TEAM - More features unlocked, fewer restrictions</li>
              <li>PRO - Most features unlocked, minimal restrictions</li>
              <li>ENTERPRISE - All features unlocked, no restrictions</li>
            </ul>
            <p className="mt-4 text-muted-foreground">
              For more details, see <code className="bg-background px-1 py-0.5 rounded">web/DEV_TOOLS_GUIDE.md</code>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
